/* ==============================================
   CEDRICK MCDONALD — MADE IN ITALY
   Main Interaction System
   ============================================== */

'use strict';

/* ==============================================
   TIMING CONFIGURATION
   All durations in milliseconds — adjust freely
   ============================================== */
const timings = {
    loadingHold:        1600,   // Minimum time loading screen is visible (lets the bar animation play)
    loadingMaxWait:     8000,   // Hard cap: never hold the loader longer than this, even if assets are slow
    loadingFade:        1400,   // Loading screen fade-out duration (matches CSS)
    introReveal:        1800,   // Delay before CTA button appears on entry screen
    enterHouseBlur:     900,    // Entry button + logo fade duration
    phaseTransition:    1200,   // Cross-fade between phases
    videoEndLead:       0.55,   // Seconds before video end to begin freeze blend
    videoFreezeBlend:   850,    // Duration of video→image freeze blend
    enterSalonReveal:   800,    // Delay after video1 ends before salon button appears
    salonPhaseReveal:   100,   // Delay after video2 ends before salon UI reveals
    uiRevealBase:       300,    // Base delay for first salon UI element
    uiRevealStagger:    180,    // Delay increment per subsequent UI element
    menuTransition:     850,    // Menu overlay open/close duration (matches CSS)
    soundFadeStep:      50,     // Interval step for audio fade in/out (ms)
    soundFadeSteps:     40,     // Number of steps for audio fade
};

/* ==============================================
   MOBILE DETECTION
   ============================================== */
const isMobile = window.matchMedia('(max-width: 680px)').matches;

/* ==============================================
   ASSET CONFIGURATION
   Replace paths once real assets are ready
   ============================================== */
const assets = Object.assign({
    heroBg:        isMobile ? 'assets/hero-bg-mob.jpg'     : 'assets/hero-bg.jpg',
    video1:        isMobile ? 'assets/video1-mob.mp4'      : 'assets/video1.mp4',
    video2:        'assets/exploring.mp4',
    hallwayEnd:    isMobile ? 'assets/hallway-end-mob.jpg' : 'assets/hallway-end.jpg',
    salonEnd:      'assets/salon-end.jpg',
    ambientTrack:  'assets/ambient.mp3'
}, window.CMC_ASSET_OVERRIDES || {});

/* ==============================================
   SOUND CONFIGURATION
   ============================================== */
const soundConfig = {
    defaultVolume:  0.38,
    fadeDuration:   2000,
};

/* ==============================================
   EDITORIAL TEXT ROTATION
   ============================================== */
const editorialTexts = (window.CMC_EDITORIAL_TEXTS && window.CMC_EDITORIAL_TEXTS.length)
    ? window.CMC_EDITORIAL_TEXTS
    : [
    {
        eyebrow: 'A Private Fashion House',
        copy: 'Step beyond the doors into an immersive world of craftsmanship, culture, and contemporary elegance.',
    },
    {
        eyebrow: 'Heritage & Craft',
        copy: 'Each garment is a testament to the Italian artisan tradition — sewn in the silence of dedicated ateliers.',
    },
    {
        eyebrow: 'The Collection',
        copy: 'Restraint is our luxury. Every line considered, every silhouette a studied dialogue between form and feeling.',
    },
];

const editorialConfig = {
    interval:   7000,   // ms between text rotations
    fadeDuration: 800,  // ms fade out duration (matches CSS)
};

/* ==============================================
   APPLICATION STATE
   ============================================== */
const state = {
    phase:              'loading',  // loading | video1 | salon | exploring
    soundOn:            false,
    menuOpen:           false,
    exploringActive:    false,
    ambientAudio:       null,
    editorialIndex:     0,
    editorialTimer:     null,
    /* House Registry state */
    registryShown:      false,
    registryJoined:     false,
    visitedSections:    new Set(),
    scrollDepth:        0,
    engagementStart:    Date.now(),
};

/* Simple cart state */
let cart = [];

/* ==============================================
   DOM ELEMENT CACHE
   ============================================== */
const el = {
    loadingScreen:      document.getElementById('loadingScreen'),

    phaseVideo1:        document.getElementById('phaseVideo1'),
    phaseExplore:       document.getElementById('phaseExplore'),
    phaseSalon:         document.getElementById('phaseSalon'),
    phaseArchive:       document.getElementById('phaseArchive'),
    phaseCollection:    document.getElementById('phaseCollection'),
    phaseJournal:       document.getElementById('phaseJournal'),
    phaseContact:       document.getElementById('phaseContact'),
    archiveDetailView:  document.getElementById('archiveDetailView'),
    archiveListWrapper: document.getElementById('archiveListWrapper'),
    detailBackBtn:      document.getElementById('detailBackBtn'),
    detailInquireBtn:   document.getElementById('detailInquireBtn'),
    detailImgWrap:      document.getElementById('detailImgWrap'),

    journalDetailView:  document.getElementById('journalDetailView'),
    journalDetailImgWrap: document.getElementById('journalDetailImgWrap'),
    journalDetailBackBtn: document.getElementById('journalDetailBackBtn'),
    journalDetailInquireBtn: document.getElementById('journalDetailInquireBtn'),
    journalContent:     document.getElementById('journalContent'),
    journalScrollTopBtn: document.getElementById('journalScrollTopBtn'),

    persistentLogo:     document.getElementById('persistentLogo'),
    persistentSound:    document.getElementById('persistentSound'),

    video1:             document.getElementById('video1'),
    video2:             document.getElementById('video2'),
    hallwayFreeze:      document.getElementById('hallwayFreeze'),
    salonFreeze:        document.getElementById('salonFreeze'),

    continueExploringBtn: document.getElementById('continueExploringBtn'),

    cartBtn:            document.getElementById('cartBtn'),
    detailAddToCartBtn: document.getElementById('detailAddToCartBtn'),
    detailStickyCart:   document.getElementById('detailStickyCart'),

    dropdownMenu:       document.getElementById('dropdownMenu'),
    menuTriggerBtn:     document.getElementById('menuTriggerBtn'),

    appointmentOverlay:  document.getElementById('appointmentOverlay'),
    appointmentBtn:      document.getElementById('appointmentBtn'),
    appointmentCloseBtn: document.getElementById('appointmentCloseBtn'),

    registryPanel:       document.getElementById('registryPanel'),
    registryCloseBtn:    document.getElementById('registryCloseBtn'),
    registryForm:        document.getElementById('registryForm'),
    registrySuccess:     document.getElementById('registrySuccess'),
    registrySalonTrigger: document.getElementById('registrySalonTrigger'),

    registryInlineArchive: document.getElementById('registryInlineArchive'),
    registryInlineForm:  document.getElementById('registryInlineForm'),

    soundChoiceOverlay: document.getElementById('soundChoiceOverlay'),
    soundChoiceOn:      document.getElementById('soundChoiceOn'),
    soundChoiceOff:     document.getElementById('soundChoiceOff'),

    editorialBlock:     document.getElementById('editorialBlock'),
    editorialEyebrow:   document.getElementById('editorialEyebrow'),
    editorialCopy:      document.getElementById('editorialCopy'),

    cursor:             document.getElementById('cursor'),
    cursorFollower:     document.getElementById('cursorFollower'),

    salonUiItems: [
        document.getElementById('uiMenuTrigger'),
        document.getElementById('uiTopRight'),
        document.getElementById('uiEditorial'),
        document.getElementById('uiEditorial1'),
        document.getElementById('uiCopyright'),
        document.getElementById('uiCopyright1'),
        document.getElementById('footer-menus1'),
    ],

    soundToggles:       document.querySelectorAll('.sound-toggle'),
};

/* ==============================================
   UTILITY
   ============================================== */

/** Resolved promise after `ms` milliseconds */
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/** Add class, optionally remove after a delay */
function addClass(element, cls) {
    if (element) element.classList.add(cls);
}

function removeClass(element, cls) {
    if (element) element.classList.remove(cls);
}

/* ==============================================
   CUSTOM CURSOR SYSTEM
   ============================================== */
(function initCursor() {
    if (window.innerWidth <= 680 || !el.cursor) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;
    let follX = mouseX;
    let follY = mouseY;
    let rafId;

    document.addEventListener('mousemove', e => {
        mouseX = e.clientX;
        mouseY = e.clientY;
        el.cursor.style.left = mouseX + 'px';
        el.cursor.style.top = mouseY + 'px';
    }, { passive: true });

    (function animateFollower() {
        const ease = 0.12;
        follX += (mouseX - follX) * ease;
        follY += (mouseY - follY) * ease;
        el.cursorFollower.style.left = follX + 'px';
        el.cursorFollower.style.top = follY + 'px';
        rafId = requestAnimationFrame(animateFollower);
    })();

    const interactiveSelectors = 'a, button, .sound-toggle, .cta-btn, .menu-trigger, .nav-link';

    document.addEventListener('mouseover', e => {
        if (e.target.closest(interactiveSelectors)) {
            addClass(el.cursor, 'is-hovering');
            addClass(el.cursorFollower, 'is-hovering');
        }
    });

    document.addEventListener('mouseout', e => {
        if (e.target.closest(interactiveSelectors)) {
            removeClass(el.cursor, 'is-hovering');
            removeClass(el.cursorFollower, 'is-hovering');
        }
    });
})();

/* ==============================================
   SOUND SYSTEM
   ============================================== */
function initSoundSystem() {
    if (assets.ambientTrack) {
        state.ambientAudio = new Audio(assets.ambientTrack);
        state.ambientAudio.loop = true;
        state.ambientAudio.volume = 0;
    }

    el.soundToggles.forEach(toggle => {
        toggle.addEventListener('click', handleSoundToggle);
        toggle.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSoundToggle();
            }
        });
    });

    refreshSoundUI();
}

function handleSoundToggle() {
    state.soundOn = !state.soundOn;

    if (state.soundOn && state.ambientAudio) {
        state.ambientAudio.play().catch(() => {
            state.soundOn = false;
        });
        fadeAudioIn(state.ambientAudio, soundConfig.defaultVolume, soundConfig.fadeDuration);
    } else if (!state.soundOn && state.ambientAudio) {
        fadeAudioOut(state.ambientAudio, () => {
            state.ambientAudio.pause();
        });
    }

    refreshSoundUI();
}

function fadeAudioIn(audio, targetVol, duration) {
    const steps = timings.soundFadeSteps;
    const stepSize = targetVol / steps;
    const interval = duration / steps;
    audio.volume = 0;
    let step = 0;
    const id = setInterval(() => {
        step++;
        audio.volume = Math.min(audio.volume + stepSize, targetVol);
        if (step >= steps) clearInterval(id);
    }, interval);
}

function fadeAudioOut(audio, onComplete) {
    const steps = timings.soundFadeSteps;
    const startVol = audio.volume;
    const stepSize = startVol / steps;
    let step = 0;
    const id = setInterval(() => {
        step++;
        audio.volume = Math.max(audio.volume - stepSize, 0);
        if (step >= steps) {
            clearInterval(id);
            if (onComplete) onComplete();
        }
    }, timings.soundFadeStep);
}

function refreshSoundUI() {
    el.soundToggles.forEach(toggle => {
        const label = toggle.querySelector('.sound-label');
        if (label) label.textContent = state.soundOn ? 'Sound On' : 'Sound Off';

        toggle.setAttribute('aria-pressed', String(state.soundOn));

        removeClass(toggle, 'sound-is-on');
        removeClass(toggle, 'sound-is-off');
        addClass(toggle, state.soundOn ? 'sound-is-on' : 'sound-is-off');
    });
}

/* ==============================================
   VIDEO MANAGEMENT
   ============================================== */
function setupVideo(videoEl, freezeEl, onComplete) {
    if (!videoEl) return;
    let nearEndFired = false;

    function handleTimeUpdate() {
        if (nearEndFired) return;
        if (!videoEl.duration) return;

        const remaining = videoEl.duration - videoEl.currentTime;
        if (remaining <= timings.videoEndLead) {
            nearEndFired = true;
            beginFreeze(videoEl, freezeEl);
        }
    }

    function handleEnded() {
        videoEl.removeEventListener('timeupdate', handleTimeUpdate);
        videoEl.removeEventListener('ended', handleEnded);

        addClass(freezeEl, 'is-visible');
        addClass(videoEl, 'is-frozen');
        if (onComplete) setTimeout(onComplete, timings.videoFreezeBlend);
    }

    videoEl.addEventListener('timeupdate', handleTimeUpdate, { passive: true });
    videoEl.addEventListener('ended', handleEnded, { once: true });
}

function beginFreeze(videoEl, freezeEl) {
    addClass(freezeEl, 'is-visible');
    setTimeout(() => {
        addClass(videoEl, 'is-frozen');
    }, timings.videoFreezeBlend);
}

function playVideo(videoEl, freezeEl, onComplete) {
    // If this phase's video/freeze elements aren't on the page yet
    // (e.g. the "Cinematic Home" section hasn't been added/published
    // yet during a section-by-section rollout), skip straight to
    // onComplete so the rest of the sequence can still proceed.
    if (!videoEl) {
        onComplete && onComplete();
        return;
    }

    removeClass(freezeEl, 'is-visible');
    removeClass(videoEl, 'is-frozen');
    videoEl.pause();
    videoEl.muted = true;
    videoEl.currentTime = 0;
    videoEl.load();

    const tryPlay = () => {
        const playPromise = videoEl.play();
        if (playPromise !== undefined) {
            playPromise.catch(() => {
                onComplete && onComplete();
            });
        }
    };

    setupVideo(videoEl, freezeEl, onComplete);
    tryPlay();
}

/* ==============================================
   PHASE TRANSITIONS
   ============================================== */
function activatePhase(phaseEl) {
    addClass(phaseEl, 'is-active');
}

function deactivatePhase(phaseEl) {
    if (!phaseEl) return;
    addClass(phaseEl, 'is-leaving');
    phaseEl.addEventListener('transitionend', () => {
        removeClass(phaseEl, 'is-active');
        removeClass(phaseEl, 'is-leaving');
    }, { once: true });
}

/* ==============================================
   SOUND CHOICE PROMPT
   ============================================== */
function showSoundChoice() {
    return new Promise(resolve => {
        if (!el.soundChoiceOverlay || !el.soundChoiceOn || !el.soundChoiceOff) {
            resolve();
            return;
        }

        addClass(el.soundChoiceOverlay, 'is-visible');

        function handleChoice(soundOn) {
            state.soundOn = soundOn;
            refreshSoundUI();

            if (soundOn && state.ambientAudio) {
                state.ambientAudio.play().catch(() => { state.soundOn = false; refreshSoundUI(); });
                fadeAudioIn(state.ambientAudio, soundConfig.defaultVolume, soundConfig.fadeDuration);
            }

            addClass(el.soundChoiceOverlay, 'is-hidden');
            setTimeout(() => {
                if (el.soundChoiceOverlay) el.soundChoiceOverlay.style.display = 'none';
                resolve();
            }, 1000);
        }

        el.soundChoiceOn.addEventListener('click', () => handleChoice(true), { once: true });
        el.soundChoiceOff.addEventListener('click', () => handleChoice(false), { once: true });
    });
}

/* ==============================================
   LOADING SEQUENCE
   ============================================== */

/**
 * Resolves once the page is "actually ready": the document has finished
 * loading (window.load) AND the entrance video (video1) has buffered
 * enough to play smoothly. Falls back gracefully -- and never waits
 * longer than timings.loadingMaxWait -- so a slow/missing video or
 * a not-yet-published section can't strand the loading screen forever.
 */
function waitForPageReady() {
    const documentReady = new Promise(resolve => {
        if (document.readyState === 'complete') {
            resolve();
        } else {
            window.addEventListener('load', resolve, { once: true });
        }
    });

    const videoReady = new Promise(resolve => {
        const v = el.video1;
        // No video element yet (section not published) -> don't block on it
        if (!v) {
            resolve();
            return;
        }
        // HAVE_FUTURE_DATA (3) or HAVE_ENOUGH_DATA (4): enough buffered to play
        if (v.readyState >= 3) {
            resolve();
            return;
        }
        v.addEventListener('loadeddata', resolve, { once: true });
        v.addEventListener('canplay', resolve, { once: true });
        // If the video errors (e.g. 404 asset), don't block the loader
        v.addEventListener('error', resolve, { once: true });
    });

    const safetyTimeout = new Promise(resolve => {
        setTimeout(resolve, timings.loadingMaxWait);
    });

    return Promise.race([
        Promise.all([documentReady, videoReady]),
        safetyTimeout
    ]);
}

async function runLoadingSequence() {
    // Run the minimum "feels intentional" hold time and the real
    // readiness check in parallel -- whichever finishes last wins.
    await Promise.all([
        wait(timings.loadingHold),
        waitForPageReady()
    ]);

    addClass(el.loadingScreen, 'is-hidden');
    await wait(timings.loadingFade);
    if (el.loadingScreen) el.loadingScreen.style.display = 'none';

    await showSoundChoice();

    addClass(el.persistentLogo, 'is-visible');
    addClass(el.persistentSound, 'is-visible');

    // "The Salon" menu link (from any other page) navigates to
    // "<home>#salon". If that hash is present on load, skip the
    // entrance video entirely and reveal the Salon directly.
    const sceneParam = new URLSearchParams(window.location.search).get('scene');
    if (sceneParam === 'salon' && el.phaseSalon) {
        // Clean up the query param so a refresh doesn't keep skipping
        // the intro.
        history.replaceState(null, '', window.location.pathname + window.location.hash);

        await startSalonPhase();
        return;
    }

    // Default ("The House", direct visit, or refresh): play the
    // entrance video, then reveal the Salon.
    await startVideo1Phase();
}

/* ==============================================
   EDITORIAL TEXT SHUFFLE
   ============================================== */
function startEditorialShuffle() {
    state.editorialTimer = setInterval(() => {
        state.editorialIndex = (state.editorialIndex + 1) % editorialTexts.length;
        rotateEditorialText();
    }, editorialConfig.interval);
}

function rotateEditorialText() {
    const block = el.editorialBlock;
    if (!block) return;

    addClass(block, 'is-fading');

    setTimeout(() => {
        const { eyebrow, copy } = editorialTexts[state.editorialIndex];
        if (el.editorialEyebrow) el.editorialEyebrow.textContent = eyebrow;
        if (el.editorialCopy) el.editorialCopy.textContent = copy;
        removeClass(block, 'is-fading');
    }, editorialConfig.fadeDuration);
}

/* ==============================================

   PHASE 2 — VIDEO 1 (Door → Hallway)
   ============================================== */
async function startVideo1Phase() {
    state.phase = 'video1';
    activatePhase(el.phaseVideo1);

    playVideo(el.video1, el.hallwayFreeze, () => {
        removeClass(el.phaseVideo1, 'is-active');
        startSalonPhase();
    });
}


/* ==============================================
   PHASE 3 — VIDEO 2 (Hallway → Salon)
   ============================================== */
function onContinueExploring() {
    if (state.exploringActive || state.phase !== 'salon') return;
    state.exploringActive = true;
    startExploringPhase();
}

async function startExploringPhase() {
    state.phase = 'exploring';
    removeClass(el.phaseSalon, 'is-active');
    activatePhase(el.phaseExplore);

    playVideo(el.video2, el.salonFreeze, async () => {
        removeClass(el.salonFreeze, 'is-visible');
        deactivatePhase(el.phaseExplore);
        activatePhase(el.phaseSalon);
        state.phase = 'salon';
        state.exploringActive = false;
    });
}

/* ==============================================
   PHASE 4 — FINAL SALON
   ============================================== */
async function startSalonPhase() {
    state.phase = 'salon';
    activatePhase(el.phaseSalon);

    // Reset engagement clock from meaningful salon entry (post doors + sound)
    state.engagementStart = Date.now();

    await wait(timings.salonPhaseReveal);

    revealSalonUI();
    updateRegistryTriggerVisibility();
}

function revealSalonUI() {
    el.salonUiItems.forEach((uiEl, index) => {
        if (!uiEl) return;
        const delay = timings.uiRevealBase + index * timings.uiRevealStagger;
        setTimeout(() => {
            addClass(uiEl, 'is-revealed');
        }, delay);
    });

    const shuffleStart = timings.uiRevealBase + el.salonUiItems.length * timings.uiRevealStagger + 1000;
    setTimeout(startEditorialShuffle, shuffleStart);
}

/* ==============================================
   MENU & DROPDOWN SYSTEM
   ============================================== */
function initMenuSystem() {
    if (el.menuTriggerBtn) {
        el.menuTriggerBtn.addEventListener('click', e => {
            e.stopPropagation();
            toggleDropdown();
        });
    }

    // Close dropdown on click outside
    document.addEventListener('click', e => {
        if (state.menuOpen) {
            const isClickInside = (el.menuTriggerBtn && el.menuTriggerBtn.contains(e.target)) ||
                                  (el.dropdownMenu && el.dropdownMenu.contains(e.target));
            if (!isClickInside) {
                closeDropdown();
            }
        }
    });

    // Close on escape key
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
            if (state.menuOpen) closeDropdown();
            closeAppointmentModal();
        }
    });

    // Persistent Logo -> Home (Salon)
    if (el.persistentLogo) {
        el.persistentLogo.style.cursor = 'pointer';
        el.persistentLogo.setAttribute('aria-hidden', 'false'); 
        el.persistentLogo.setAttribute('role', 'button');
        el.persistentLogo.setAttribute('aria-label', 'Return to Salon');

        el.persistentLogo.addEventListener('click', () => {
            if (state.phase === 'salon') {
                if (state.menuOpen) closeDropdown();
                return;
            }

            if (state.phase === 'archive') {
                transitionArchiveToSalon();
            } else if (state.phase === 'collection') {
                transitionCollectionToSalon();
            } else if (state.phase === 'journal' || state.phase === 'contact') {
                transitionInnerToSalon();
            } else if (state.phase === 'exploring' || state.phase === 'video1') {
                replayHouseSequence();
            } else if (state.phase !== 'salon') {
                // Robust fallback: force return to main Salon from any unknown/other phase
                if (typeof closeProductDetail === 'function') closeProductDetail();
                if (typeof closeJournalDetail === 'function') closeJournalDetail();
                document.querySelectorAll('.phase.is-active').forEach(ph => removeClass(ph, 'is-active'));
                removeClass(document.body, 'archive-active');
                removeClass(document.body, 'detail-active');
                if (el.phaseSalon) addClass(el.phaseSalon, 'is-active');
                state.phase = 'salon';
                startEditorialShuffle();
                updateRegistryTriggerVisibility();
            }

            if (state.menuOpen) closeDropdown();
        });
    }  

    initNestedDropdowns();

    // Handle dropdown links. Every item now has a real href (see
    // cinematic-header.liquid), so by default these are plain links and
    // the browser navigates normally. "The House" / "The Salon" get a
    // little extra JS so that, when you're ALREADY on the home page,
    // clicking them transitions in place instead of doing a full
    // page reload.
    if (el.dropdownMenu) {
    el.dropdownMenu.querySelectorAll('.dropdown-link:not(.dropdown-parent-btn)').forEach(link => {
            link.addEventListener('click', e => {
                const targetId = link.getAttribute('id');
                const onHomePage = !!(el.phaseVideo1 && el.phaseSalon);

                if (targetId === 'navTheHouse' && onHomePage) {
                    e.preventDefault();
                    replayHouseSequence();
                } else if (targetId === 'navTheSalon' && onHomePage) {
                    e.preventDefault();
                    if (state.phase !== 'salon') jumpToSalon();
                }
                // Otherwise: let the link navigate normally to its href.

                closeDropdown();
            });
        });
    }
}

function toggleDropdown() {
    if (state.menuOpen) {
        closeDropdown();
    } else {
        openDropdown();
    }
}

function openDropdown() {
    state.menuOpen = true;
    if (el.dropdownMenu) {
        addClass(el.dropdownMenu, 'is-open');
        el.dropdownMenu.removeAttribute('aria-hidden');
    }
    // Quiet dismiss of registry when menu opens (user is navigating)
    if (el.registryPanel && el.registryPanel.classList.contains('is-visible')) {
        dismissRegistryPanel(false);
    }
    if (el.menuTriggerBtn) {
        el.menuTriggerBtn.setAttribute('aria-expanded', 'true');
        addClass(el.menuTriggerBtn, 'is-active');
        const label = el.menuTriggerBtn.querySelector('.menu-trigger__label');
        if (label) label.textContent = 'Close';
    }
}

function closeDropdown() {
    state.menuOpen = false;
    if (el.dropdownMenu) {
        removeClass(el.dropdownMenu, 'is-open');
        el.dropdownMenu.setAttribute('aria-hidden', 'true');
    }
    if (el.menuTriggerBtn) {
        el.menuTriggerBtn.setAttribute('aria-expanded', 'false');
        removeClass(el.menuTriggerBtn, 'is-active');
        const label = el.menuTriggerBtn.querySelector('.menu-trigger__label');
        if (label) label.textContent = 'Menu';
    }
    closeAllSubmenus();
}

function closeAllSubmenus() {
    if (!el.dropdownMenu) return;
    el.dropdownMenu.querySelectorAll('.dropdown-item.has-children').forEach(item => {
        removeClass(item, 'is-open');
        const btn = item.querySelector('.dropdown-parent-btn');
        const submenu = item.querySelector('.dropdown-submenu');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (submenu) submenu.setAttribute('aria-hidden', 'true');
    });
}

function initNestedDropdowns() {
    if (!el.dropdownMenu) return;

    el.dropdownMenu.querySelectorAll('.dropdown-parent-btn').forEach(btn => {
        btn.addEventListener('click', e => {
            e.preventDefault();
            e.stopPropagation();

            const item = btn.closest('.dropdown-item');
            if (!item) return;

            const isOpen = item.classList.contains('is-open');

            // Only one submenu open at a time
            closeAllSubmenus();

            if (!isOpen) {
                addClass(item, 'is-open');
                btn.setAttribute('aria-expanded', 'true');
                const submenu = item.querySelector('.dropdown-submenu');
                if (submenu) submenu.setAttribute('aria-hidden', 'false');
            }
        });
    });
}

/**
 * Already on the home page, but in the entrance video / exploring
 * phase -- stop whatever is playing and snap straight to the Salon.
 * Used by "The Salon" menu link.
 */
function jumpToSalon() {
    if (el.video1) el.video1.pause();
    if (el.video2) el.video2.pause();

    document.querySelectorAll('.phase.is-active').forEach(ph => removeClass(ph, 'is-active'));
    removeClass(el.phaseVideo1, 'is-leaving');
    removeClass(el.phaseExplore, 'is-leaving');

    state.exploringActive = false;
    startSalonPhase();
}

async function replayHouseSequence() {
    if (state.phase !== 'salon' && state.phase !== 'archive' && state.phase !== 'collection' && state.phase !== 'journal' && state.phase !== 'contact') return;

    // Remove detail active state from body if open
    closeProductDetail();
    closeJournalDetail();

    // Remove archive active state from body
    removeClass(document.body, 'archive-active');

    // Reset contact form if applicable
    const contactForm = document.getElementById('contactForm');
    const contactSuccess = document.getElementById('contactSuccess');
    if (contactForm) removeClass(contactForm, 'is-hidden');
    if (contactSuccess) {
        contactSuccess.setAttribute('aria-hidden', 'true');
        removeClass(contactSuccess, 'is-visible');
    }

    // Fade out salon UI items
    el.salonUiItems.forEach(uiEl => {
        if (uiEl) removeClass(uiEl, 'is-revealed');
    });

    if (state.editorialTimer) {
        clearInterval(state.editorialTimer);
        state.editorialTimer = null;
    }

    removeClass(el.phaseSalon, 'is-active');
    removeClass(el.phaseArchive, 'is-active');
    removeClass(el.phaseCollection, 'is-active');
    removeClass(el.phaseJournal, 'is-active');
    removeClass(el.phaseContact, 'is-active');
    await wait(800);

    // Registry: allow potential re-trigger on fresh house journey (respects LS joined/dismissed)
    if (localStorage.getItem(REGISTRY_LS_DISMISSED) !== 'true') {
        state.registryShown = false;
        if (el.registryPanel) removeClass(el.registryPanel, 'is-visible');
    }
    state.scrollDepth = 0;
    state.visitedSections.clear();
    state.engagementStart = Date.now();

    updateRegistryTriggerVisibility();

    // Replay
    startVideo1Phase();
}

/* ==============================================
   PHASE 5 — ARCHIVE SYSTEM
   ============================================== */
const productCatalog = {
    'pumps': {
        num: '01',
        category: 'Pumps',
        title: 'Minimalist Travertine Pump',
        image: 'assets/archive-pumps.png',
        alt: 'Minimalist Travertine Pump on travertine pedestal',
        story: 'The Minimalist Travertine Pump represents a studied dialogue between classic structural stability and fluid grace. Inspired by the architectural lines of modern Italian design, each heel is sculpted from solid wood before being wrapped in hand-selected Tuscan calfskin. The result is a shoe that does not merely support, but elevates the human silhouette, capturing the essence of private luxury.',
        material: '100% Full-Grain Travertine Calfskin',
        color: 'Travertine Beige',
        atelier: 'Florence Atelier, Italy',
        availability: 'Limited edition of 12 numbered pairs',
        price: 1250
    },
    'sandals': {
        num: '02',
        category: 'Sandals',
        title: 'Tuscan Dappled Sandal',
        image: 'assets/archive-sandals.png',
        alt: 'Curated strappy high heels in warm sunlight',
        story: 'Designed under the influence of light and shadow, the Tuscan Dappled Sandal is a tribute to warm Italian summers. The delicate, asymmetrical leather straps are positioned to embrace the foot in an embrace of sheer comfort and geometric precision. Hand-burnished by artisans using traditional vegetable dyes, the leather will develop a rich, personal patina over time, recording the unique steps of its wearer.',
        material: 'Vegetable-Tanned Vachetta Leather',
        color: 'Sun-Dappled Bronze',
        atelier: 'Pisa Atelier, Italy',
        availability: 'Bespoke custom order only',
        price: 980
    },
    'mules': {
        num: '03',
        category: 'Mules',
        title: 'Oak Parquet Silk Mule',
        image: 'assets/archive-mules.png',
        alt: 'Luxury folded silk slippers on wood parquet',
        story: 'Crafted from the finest silk satin woven on the shores of Lake Como, this mule is a masterclass in luxurious restraint. The front panel features a hand-folded drape that mimics the soft folds of Italian classical marble drapery. Lined in velvet-soft calfskin and set on a low, quiet leather heel, it is designed for the absolute privacy of the residence and the effortless transition to evening gatherings.',
        material: 'Como Silk Satin & Calfskin Lining',
        color: 'Midnight Champagne',
        atelier: 'Como Atelier, Italy',
        availability: 'Limited edition of 18 numbered pairs',
        price: 890
    },
    'boots': {
        num: '04',
        category: 'Boots',
        title: 'Sculptural Calfskin Boot',
        image: 'assets/archive-boots.png',
        alt: 'Minimalist leather ankle boots with wooden heel',
        story: 'The Sculptural Calfskin Boot is a study in raw, uncompromising geometry. Using a single piece of premium Italian calfskin, our master artisans shape the boot over custom wooden lasts to achieve an unbroken silhouette. The statement heel is carved from solid, sustainably sourced Venetian walnut, showcasing the natural grain of the wood against the deep matte black leather.',
        material: 'Exquisite Brushed Calfskin & Solid Walnut',
        color: 'Atelier Charcoal',
        atelier: 'Venice Atelier, Italy',
        availability: 'Limited edition of 8 numbered pairs',
        price: 1650
    },
    'future': {
        num: '05',
        category: 'Future Collections',
        title: 'Monolith Sandal',
        image: 'placeholder',
        alt: 'Monolith Sandal Draft Visual',
        story: 'An experimental study in mass and negative space, the Monolith Sandal will launch as part of our upcoming Fall collection. Breaking away from traditional slender heels, it utilizes a solid, hollowed-out block sole that challenges perceptions of weight and movement. Previewed here in the silent drafts of the Residence.',
        material: 'Eco-Textured Sustainable Grain',
        color: 'Raw Alabaster',
        atelier: 'Milan Residence Studio, Italy',
        availability: 'Coming Fall 2026'
    },
    'loafers': {
        num: '05',
        category: 'Loafers',
        title: 'Venetian Hand-Burnished Loafer',
        image: 'assets/archive-pumps.png',
        alt: 'Hand-burnished leather loafers',
        story: 'A timeless penny loafer executed in the finest vegetable-tanned Tuscan calfskin. Each pair is hand-burnished for depth of color and features discreet hand-stitching along the apron. The leather sole is finished with a subtle rubber insert for city traction while preserving the quiet elegance of traditional construction.',
        material: 'Vegetable-Tanned Tuscan Calfskin',
        color: 'Cognac Brown',
        atelier: 'Venice Atelier, Italy',
        availability: 'Limited edition of 24 numbered pairs',
        price: 1120
    },
    'heels': {
        num: '06',
        category: 'Heels',
        title: 'Sculpted Suede Stiletto',
        image: 'assets/archive-sandals.png',
        alt: 'Sculpted suede stiletto heels',
        story: 'An architectural statement heel rising 90mm in the softest midnight suede. The sculpted form is achieved through a proprietary last developed over three seasons. A single brushed metal accent at the heel counter provides the only ornamentation, allowing the purity of line to speak for itself.',
        material: 'Premium Italian Suede & Metal Accent',
        color: 'Midnight Suede',
        atelier: 'Milan Atelier, Italy',
        availability: 'Limited edition of 15 numbered pairs',
        price: 1340
    },
    'oxfords': {
        num: '07',
        category: 'Oxfords',
        title: 'Midnight Leather Oxford',
        image: 'assets/archive-mules.png',
        alt: 'Midnight leather oxford shoes',
        story: 'A masterclass in formal restraint. Five-eyelet oxford cut from supple black calf with Goodyear welt construction for longevity and resoling. The last is a modern interpretation of a 1950s Florentine shape—elegant yet comfortable for extended wear in the private residence or evening occasions.',
        material: 'Full-Grain Black Calfskin',
        color: 'Midnight Black',
        atelier: 'Florence Atelier, Italy',
        availability: 'Limited edition of 20 numbered pairs',
        price: 1280
    },
    'flats': {
        num: '08',
        category: 'Flats',
        title: 'Minimalist Calfskin Ballet Flat',
        image: 'assets/archive-boots.png',
        alt: 'Minimalist calfskin ballet flats',
        story: 'The purest expression of the ballet flat. Cut from ultra-soft full-grain calfskin with a hidden elastic gore for a second-skin fit. A discreet gold-stamped logo at the insole is the only branding. Designed for absolute comfort during long evenings of private entertaining.',
        material: 'Ultra-Soft Full-Grain Calfskin',
        color: 'Pale Ivory',
        atelier: 'Como Atelier, Italy',
        availability: 'Limited edition of 30 numbered pairs',
        price: 720
    }
};

const journalCatalog = {
    'philosophy': {
        category: 'Philosophy',
        title: 'More than Commerce, an Emotion',
        htmlMedia: '<video src="assets/studio.mp4" autoplay muted loop playsinline preload="auto" style="width: 100%; height: 100%; object-fit: cover;"></video>',
        story: '<p>We believe the House should feel authored, immersive, and emotionally layered. This is a space for narrative, where the product is a consequence of the story, not the origin. Here, we invite you to experience the emotional depth that underpins our collections.</p><p>Our approach to luxury goes beyond aesthetics; it is an investigation into identity, craft, and the silent language of objects. Every piece we create is designed to evoke a feeling, to connect the wearer to a larger cinematic world where tradition and avant-garde sensibilities coalesce.</p><p>By prioritizing emotion over mere commerce, we ensure that each collection stands as a testament to our dedication to the art form. We invite our patrons not just to wear a product, but to inhabit a meticulously curated universe of thought and design.</p>'
    },
    'silence': {
        category: 'Cinematic Atmosphere',
        title: 'The Silence of the Halls',
        htmlMedia: `<img src="${assets.hallwayEnd}" alt="The Residence Hallway" style="width: 100%; height: 100%; object-fit: cover;">`,
        story: '<p>Walking through the residence, you feel the weight of history and the lightness of modern design. The silence allows the mind to wander, connecting past traditions with future visions in a continuous flow of creative energy.</p><p>The echoes of Florence and Milan permeate the walls, acting as the ultimate canvas for our artistic directors. In these hushed corridors, the absence of noise becomes a presence in itself—a deliberate void that heightens the senses and brings focus to the minutiae of our craft.</p><p>It is in this profound silence that the boldest ideas are born. Away from the cacophony of the outside world, the House cultivates an atmosphere of intense introspection, allowing our creations to speak with quiet, unshakeable confidence.</p>'
    },
    'light': {
        category: 'Inspiration',
        title: 'Light & Shadow',
        htmlMedia: '<img src="assets/salon-end.jpg" alt="The Salon" style="width: 100%; height: 100%; object-fit: cover;">',
        story: '<p>Our color palette and silhouettes are directly inspired by the interplay of natural light across Italian stone. The way shadows lengthen in the late afternoon informs the drape of our fabrics and the structure of our forms.</p><p>We design for moments suspended in time, where grace is captured in stillness. The chiaroscuro effect—so central to Renaissance art—finds new life in the architectural lines of our footwear and the sculptural volumes of our leather goods.</p><p>Every curve is meticulously calibrated to catch the light, transforming a static object into a dynamic piece of wearable sculpture that shifts character as the sun moves across the sky.</p>'
    },
    'heritage': {
        category: 'Heritage',
        title: 'The Archives of Milan',
        htmlMedia: `<img src="${assets.heroBg}" alt="Vintage sketch" style="width: 100%; height: 100%; object-fit: cover;">`,
        story: '<p>Before a single cut is made, there is a study of what came before. Our heritage is not a static museum, but a living vocabulary. By examining vintage lasts and historical patterns, we construct the foundation for contemporary elegance.</p><p>The archive serves as our compass, ensuring every new creation remains anchored in the unshakeable principles of the House. We look back not with nostalgia, but with a forensic curiosity, extracting the essence of past masterpieces to solve modern design challenges.</p><p>This continuous dialogue between eras ensures that our visual language remains cohesive, authoritative, and deeply rooted in the grand tradition of Italian luxury.</p>'
    },
    'material': {
        category: 'Material',
        title: 'The Alchemy of Leather',
        htmlMedia: '<img src="assets/archive-boots.png" alt="Leather textures" style="width: 100%; height: 100%; object-fit: cover;">',
        story: '<p>Selecting hides is an act of tactile intuition. Our artisans search for the perfect grain, understanding how it will age, soften, and mold to the wearer over decades of use.</p><p>We treat our materials with reverence, opting for vegetable-tanning processes that celebrate the natural character and beautiful imperfections of real Italian leather. The alchemical transformation from raw hide to exquisite finished piece is a closely guarded process, involving weeks of resting, tumbling, and hand-finishing.</p><p>The resulting patina is entirely unique to each piece, ensuring that no two items are exactly alike. It is a celebration of organic material pushed to its absolute aesthetic limit.</p>'
    },
    'craft': {
        category: 'Craft',
        title: 'The Hand That Guides',
        htmlMedia: '<video src="assets/exploring.mp4" loop autoplay muted playsinline preload="auto" style="width: 100%; height: 100%; object-fit: cover;"></video>',
        story: '<p>True luxury cannot be hurried. It requires the steady hand of a master shoemaker, whose muscle memory guides the awl and thread through dense leather with quiet precision.</p><p>Craftsmanship for us is an act of devotion, honoring techniques passed down through generations in the historic ateliers of Italy. In an era of mass production, we remain staunchly committed to the human touch. Every stitch is a deliberate choice, every seam a testament to years of rigorous training.</p><p>The unseen hours poured into each creation imbue it with a soul that machines simply cannot replicate. It is this invisible architecture of care that defines the true value of the House.</p>'
    }
};

async function transitionSalonToArchive() {
    if (state.phase !== 'salon') return;
    state.phase = 'archive';
    markRegistrySectionVisit('archive');
    updateRegistryTriggerVisibility();
    // Quietly dismiss registry panel when leaving salon (non-interruptive)
    if (el.registryPanel && el.registryPanel.classList.contains('is-visible')) {
        dismissRegistryPanel(false);
    }

    // Add body class to hide the salon's editorial copy smoothly
    addClass(document.body, 'archive-active');

    // Cross-fade the phases
    deactivatePhase(el.phaseSalon);
    await wait(400); // slight stagger for elegant crossover
    activatePhase(el.phaseArchive);

    // Stop editorial rotation to save battery / performance
    if (state.editorialTimer) {
        clearInterval(state.editorialTimer);
        state.editorialTimer = null;
    }
}

async function transitionArchiveToSalon() {
    if (state.phase !== 'archive') return;
    state.phase = 'salon';

    // Collapse active detail page state
    closeProductDetail();

    // Remove the body class to reveal the salon's editorial copy
    removeClass(document.body, 'archive-active');

    // Cross-fade the phases
    deactivatePhase(el.phaseArchive);
    await wait(400);
    activatePhase(el.phaseSalon);

    // Restart editorial text shuffle
    startEditorialShuffle();

    // Refresh engagement timer so registry auto doesn't pop immediately on re-entry;
    // respects prior LS dismissal (auto only). Button entry remains available.
    if (localStorage.getItem(REGISTRY_LS_DISMISSED) !== 'true') {
        state.registryShown = false;
        state.engagementStart = Date.now();
    }
    updateRegistryTriggerVisibility();
}

async function transitionSalonToCollection() {
    if (state.phase !== 'salon') return;
    state.phase = 'collection';
    markRegistrySectionVisit('collection');
    updateRegistryTriggerVisibility();
    if (el.registryPanel && el.registryPanel.classList.contains('is-visible')) {
        dismissRegistryPanel(false);
    }

    addClass(document.body, 'archive-active');

    deactivatePhase(el.phaseSalon);
    await wait(400);
    activatePhase(el.phaseCollection);

    if (state.editorialTimer) {
        clearInterval(state.editorialTimer);
        state.editorialTimer = null;
    }
}

async function transitionCollectionToSalon() {
    if (state.phase !== 'collection') return;
    state.phase = 'salon';

    closeProductDetail();

    removeClass(document.body, 'archive-active');

    deactivatePhase(el.phaseCollection);
    await wait(400);
    activatePhase(el.phaseSalon);

    startEditorialShuffle();

    if (localStorage.getItem(REGISTRY_LS_DISMISSED) !== 'true') {
        state.registryShown = false;
        state.engagementStart = Date.now();
    }
    updateRegistryTriggerVisibility();
}

async function transitionArchiveToCollection() {
    if (state.phase !== 'archive') return;
    state.phase = 'collection';

    closeProductDetail();
    deactivatePhase(el.phaseArchive);
    await wait(400);
    activatePhase(el.phaseCollection);
}

async function transitionCollectionToArchive() {
    if (state.phase !== 'collection') return;
    state.phase = 'archive';

    closeProductDetail();
    deactivatePhase(el.phaseCollection);
    await wait(400);
    activatePhase(el.phaseArchive);
}

async function transitionSalonToJournal() {
    if (state.phase !== 'salon') return;
    state.phase = 'journal';
    markRegistrySectionVisit('journal');
    updateRegistryTriggerVisibility();
    if (el.registryPanel && el.registryPanel.classList.contains('is-visible')) {
        dismissRegistryPanel(false);
    }

    // Reuse archive-active class to hide salon UI
    addClass(document.body, 'archive-active');

    deactivatePhase(el.phaseSalon);
    await wait(400);
    activatePhase(el.phaseJournal);

    if (state.editorialTimer) {
        clearInterval(state.editorialTimer);
        state.editorialTimer = null;
    }
}

async function transitionBetweenInnerPhases(from, to) {
    if (state.phase !== from) return;
    state.phase = to;
    markRegistrySectionVisit(to);

    const phaseMap = { 
        archive: el.phaseArchive, 
        collection: el.phaseCollection,
        journal: el.phaseJournal, 
        contact: el.phaseContact 
    };
    const fromEl = phaseMap[from];
    const toEl = phaseMap[to];

    if (from === 'archive' || from === 'collection') closeProductDetail();
    if (from === 'journal') closeJournalDetail();

    deactivatePhase(fromEl);
    await wait(400);
    activatePhase(toEl);
}

async function transitionInnerToSalon() {
    const currentPhase = state.phase;
    if (currentPhase !== 'journal' && currentPhase !== 'contact' && currentPhase !== 'archive' && currentPhase !== 'collection') return;
    state.phase = 'salon';

    if (currentPhase === 'journal') closeJournalDetail();
    if (currentPhase === 'archive' || currentPhase === 'collection') closeProductDetail();

    removeClass(document.body, 'archive-active');

    let fromEl;
    if (currentPhase === 'journal') fromEl = el.phaseJournal;
    else if (currentPhase === 'contact') fromEl = el.phaseContact;
    else if (currentPhase === 'archive') fromEl = el.phaseArchive;
    else fromEl = el.phaseCollection;

    deactivatePhase(fromEl);
    await wait(400);
    activatePhase(el.phaseSalon);

    startEditorialShuffle();

    updateRegistryTriggerVisibility();
}

async function transitionSalonToContact() {
    if (state.phase !== 'salon') return;
    state.phase = 'contact';
    updateRegistryTriggerVisibility();
    if (el.registryPanel && el.registryPanel.classList.contains('is-visible')) {
        dismissRegistryPanel(false);
    }

    addClass(document.body, 'archive-active');

    deactivatePhase(el.phaseSalon);
    await wait(400);
    activatePhase(el.phaseContact);

    if (state.editorialTimer) {
        clearInterval(state.editorialTimer);
        state.editorialTimer = null;
    }
}

async function transitionContactToSalon() {
    if (state.phase !== 'contact') return;
    state.phase = 'salon';

    removeClass(document.body, 'archive-active');

    deactivatePhase(el.phaseContact);
    await wait(400);
    activatePhase(el.phaseSalon);

    startEditorialShuffle();
}

function openJournalDetail(card) {
    if (!card) return;

    const category = card.getAttribute('data-category') || '';
    const title    = card.getAttribute('data-title') || '';
    const storyHtml = card.getAttribute('data-story') || '';
    const image    = card.getAttribute('data-image') || '';
    const imageAlt = card.getAttribute('data-image-alt') || '';
    const video    = card.getAttribute('data-video') || '';

    if (el.journalDetailImgWrap) {
        if (video) {
            el.journalDetailImgWrap.innerHTML = `<video src="${video}" autoplay muted loop playsinline preload="auto" style="width:100%;height:100%;object-fit:cover;"></video>`;
        } else if (image) {
            el.journalDetailImgWrap.innerHTML = `<img src="${image}" alt="${imageAlt}" style="width:100%;height:100%;object-fit:cover;">`;
        } else {
            el.journalDetailImgWrap.innerHTML = '';
        }
    }

    const catEl = document.getElementById('journalDetailCategory');
    if (catEl) catEl.textContent = category;

    const titleEl = document.getElementById('journalDetailTitle');
    if (titleEl) titleEl.textContent = title;

    const bodyEl = document.getElementById('journalDetailStory');
    if (bodyEl) bodyEl.innerHTML = storyHtml;

    if (el.journalDetailView) el.journalDetailView.removeAttribute('aria-hidden');
    
    // Small delay to allow display change before transition
    setTimeout(() => {
        addClass(document.body, 'journal-detail-active');
    }, 10);
    
    if (el.journalContent) el.journalContent.setAttribute('aria-hidden', 'true');

    const detailScroll = document.getElementById('journalDetailScrollContainer');
    if (detailScroll) detailScroll.scrollTop = 0;
}

function closeJournalDetail() {
    removeClass(document.body, 'journal-detail-active');
    
    if (el.journalContent) el.journalContent.removeAttribute('aria-hidden');

    // Quietly dismiss registry panel during detail view (cinematic priority)
    if (el.registryPanel && el.registryPanel.classList.contains('is-visible')) {
        dismissRegistryPanel(false);
    }

    // Wait for transition to finish
    setTimeout(() => {
        if (!document.body.classList.contains('journal-detail-active')) {
            if (el.journalDetailView) el.journalDetailView.setAttribute('aria-hidden', 'true');
        }
    }, 820);
}

function initJournalSystem() {
    const journalCards = document.querySelectorAll('.journal-card');
    journalCards.forEach(card => {
        const readBtn = card.querySelector('.nav-cta');
        const trigger = readBtn ? readBtn : card;
        
        trigger.addEventListener('click', () => {
            openJournalDetail(card);
        });
    });

    if (el.journalDetailBackBtn) {
        el.journalDetailBackBtn.addEventListener('click', closeJournalDetail);
    }

    if (el.journalDetailInquireBtn) {
        el.journalDetailInquireBtn.addEventListener('click', openAppointmentModal);
    }

    const detailScroll = document.getElementById('journalDetailScrollContainer');
    if (detailScroll && el.journalScrollTopBtn) {
        detailScroll.addEventListener('scroll', () => {
            if (detailScroll.scrollTop > 400) {
                addClass(el.journalScrollTopBtn, 'is-visible');
            } else {
                removeClass(el.journalScrollTopBtn, 'is-visible');
            }
        });

        el.journalScrollTopBtn.addEventListener('click', () => {
            detailScroll.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }
}

function openProductDetail(card) {
    if (!card) return;

    const data = {
        num:          card.getAttribute('data-num') || '',
        category:     card.getAttribute('data-category-label') || '',
        title:        card.getAttribute('data-title') || '',
        price:        card.getAttribute('data-price') || '',
        story:        card.getAttribute('data-story') || '',
        material:     card.getAttribute('data-material') || '',
        color:        card.getAttribute('data-color') || '',
        atelier:      card.getAttribute('data-atelier') || '',
        availability: card.getAttribute('data-availability') || '',
        sizing:       card.getAttribute('data-sizing') || '',
        shipping:     card.getAttribute('data-shipping') || '',
        image:        card.getAttribute('data-image') || '',
        imageAlt:     card.getAttribute('data-image-alt') || '',
        variantId:     card.getAttribute('data-variant-id') || '',
        productHandle: card.getAttribute('data-product-handle') || ''
    };

    // 1. Populate details
    // 1. Populate details
    if (el.detailImgWrap) {
        let images = [];
        try {
            images = JSON.parse(card.getAttribute('data-images') || '[]');
        } catch (e) {
            images = [];
        }
        if (images.length === 0 && data.image) images = [data.image];

        if (window.detailSwiperInstance) {
            window.detailSwiperInstance.destroy(true, true);
            window.detailSwiperInstance = null;
        }

        if (images.length > 0) {
            const slidesHtml = images.map(src =>
                `<div class="swiper-slide"><div style="width: 100%; overflow: hidden"><img src="${src}" alt="${data.imageAlt}"></div></div>`
            ).join('');

            el.detailImgWrap.innerHTML = `
                <div class="swiper detail-swiper" id="detailSwiper">
                    <div class="swiper-wrapper">${slidesHtml}</div>
                    ${images.length > 1 ? '<div class="swiper-button-prev"> </div><div class="swiper-button-next"> </div>' : ''}
                </div>
            `;

            const swiperEl = document.getElementById('detailSwiper');
            if (swiperEl && typeof Swiper !== 'undefined') {
                window.detailSwiperInstance = new Swiper(swiperEl, {
                    slidesPerView: 1,
                    loop: false,
                    speed: 350,
                    pagination: images.length > 1
                        ? { el: swiperEl.querySelector('.swiper-pagination'), clickable: true }
                        : false,
                    navigation: images.length > 1
                        ? { nextEl: swiperEl.querySelector('.swiper-button-next'), prevEl: swiperEl.querySelector('.swiper-button-prev') }
                        : false,
                });
            }
        } else {
            el.detailImgWrap.innerHTML = `
                <div class="future-visual placeholder-wrap" style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; border: 1px dashed rgba(255, 255, 255, 0.12); background: rgba(255, 255, 255, 0.02);">
                    <div class="future-visual" style="display: flex; align-items: center; justify-content: center; width: 80px; height: 80px; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 50%;">
                        <span class="future-letter" style="font-family: var(--ff-serif); font-size: 1.1rem; font-weight: 300; letter-spacing: 0.1em; color: var(--clr-gold-dim);">CD</span>
                    </div>
                </div>
            `;
        }
    }

    const numEl = document.getElementById('detailProductNum');
    if (numEl) numEl.textContent = data.num;

    const catEl = document.getElementById('detailProductCategory');
    if (catEl) catEl.textContent = data.category;

    const titleEl = document.getElementById('detailProductTitle');
    if (titleEl) titleEl.textContent = data.title;

    const priceEl = document.getElementById('detailProductPrice');
    if (priceEl) priceEl.innerHTML = data.price;

    const storyEl = document.getElementById('detailProductStory');
    if (storyEl) storyEl.textContent = data.story;

    const matEl = document.getElementById('detailProductMaterial');
    if (matEl) matEl.textContent = data.material;

    const colEl = document.getElementById('detailProductColor');
    if (colEl) colEl.textContent = data.color;

    const atlEl = document.getElementById('detailProductAtelier');
    if (atlEl) atlEl.textContent = data.atelier;

    const avEl = document.getElementById('detailProductAvailability');
    if (avEl) avEl.textContent = data.availability;

    const sizeEl = document.getElementById('detailProductSizing');
    if (sizeEl) sizeEl.textContent = data.sizing;

    const shipEl = document.getElementById('detailProductShipping');
    if (shipEl) shipEl.textContent = data.shipping;

    // Set current variant + handle for detail Add to Cart (real Shopify cart, see cmc-cart.js)
    if (el.detailAddToCartBtn) {
        el.detailAddToCartBtn.setAttribute('data-variant-id', data.variantId);
        el.detailAddToCartBtn.setAttribute('data-product-handle', data.productHandle);
    }

    // Toggle CTA button based on context: Collection page uses Add to Cart, Archive uses Inquire
    const isCollectionContext = !!(el.phaseCollection && el.phaseCollection.classList.contains('is-active'));
    if (el.detailInquireBtn) {
        el.detailInquireBtn.style.display = isCollectionContext ? 'none' : '';
    }
    if (el.detailStickyCart) {
        el.detailStickyCart.style.display = isCollectionContext ? '' : 'none';
    }

    // Reset story expander state
    if (storyEl) {
        removeClass(storyEl, 'is-expanded');
    }
    const storyMoreBtn = document.getElementById('storyMoreBtn');
    if (storyMoreBtn) {
        storyMoreBtn.setAttribute('aria-expanded', 'false');
        const textSpan = storyMoreBtn.querySelector('.btn-text');
        const iconSpan = storyMoreBtn.querySelector('.btn-icon');
        if (textSpan) textSpan.textContent = 'Read More';
        if (iconSpan) iconSpan.textContent = '↓';
        storyMoreBtn.style.display = 'inline-flex';
    }

    // 2. Add class to transition elements
    addClass(document.body, 'detail-active');
    document.querySelector('html').classList.add('detail-active1');

    // Add active1 class to the currently visible archive-wrapper
    document.querySelectorAll('.phase.is-active .archive-wrapper, .overlay-foter, #footer-menus1').forEach(wrapper => {
        addClass(wrapper, 'active1');
    });
    
    // Set accessibility
    if (el.archiveDetailView) {
        el.archiveDetailView.removeAttribute('aria-hidden');
    }
    if (el.archiveListWrapper) {
        el.archiveListWrapper.setAttribute('aria-hidden', 'true');
    }

    // Scroll detail container back to top
    const detailScrollContainer = document.getElementById('detailScrollContainer');
    if (detailScrollContainer) {
        detailScrollContainer.scrollTop = 0;
    }
}

function closeProductDetail() {
    removeClass(document.body, 'detail-active');
    document.querySelector('html').classList.remove('detail-active1');

    // Remove active1 class from any archive-wrapper
    document.querySelectorAll('.archive-wrapper.active1, .overlay-foter.active1, #footer-menus1.active1').forEach(wrapper => {
        removeClass(wrapper, 'active1');
    });
    
    if (el.archiveDetailView) {
        el.archiveDetailView.setAttribute('aria-hidden', 'true');
    }

    // Quietly dismiss registry panel during detail view (cinematic priority)
    if (el.registryPanel && el.registryPanel.classList.contains('is-visible')) {
        dismissRegistryPanel(false);
    }
    if (el.archiveListWrapper) {
        el.archiveListWrapper.removeAttribute('aria-hidden');
    }
}

/* ==============================================
   CART SYSTEM (minimal)
   ============================================== */
function addToCart(productId) {
    const product = productCatalog[productId];
    if (!product) return;

    // Prevent duplicates for simplicity (or allow multiples)
    const existing = cart.findIndex(item => item.id === productId);
    if (existing !== -1) {
        cart[existing].qty = (cart[existing].qty || 1) + 1;
    } else {
        cart.push({ ...product, id: productId, qty: 1 });
    }

    updateCartUI();
    // Simple feedback
    const btns = document.querySelectorAll(`.add-to-cart-btn[data-product-id="${productId}"]`);
    btns.forEach(b => {
        const original = b.textContent;
        b.textContent = 'Added ✓';
        b.disabled = true;
        setTimeout(() => {
            b.textContent = original;
            b.disabled = false;
        }, 1200);
    });

    // Feedback on detail Add to Cart button (if visible for this product)
    if (el.detailAddToCartBtn && el.detailAddToCartBtn.dataset.currentProduct === productId) {
        const labelSpan = el.detailAddToCartBtn.querySelector('.cta-label');
        if (labelSpan) {
            const original = labelSpan.textContent.trim();
            labelSpan.textContent = 'Added ✓';
            el.detailAddToCartBtn.disabled = true;
            setTimeout(() => {
                labelSpan.textContent = original;
                el.detailAddToCartBtn.disabled = false;
            }, 1200);
        }
    }
}

function updateCartUI() {
    if (!el.cartBtn) return;
    const count = cart.reduce((sum, item) => sum + (item.qty || 1), 0);
    let badge = el.cartBtn.querySelector('.cart-count');
    if (!badge) {
        badge = document.createElement('span');
        badge.className = 'cart-count';
        badge.style.cssText = 'position:absolute;top:-4px;right:-4px;background:var(--clr-gold);color:#000;font-size:9px;padding:1px 5px;border-radius:999px;min-width:14px;text-align:center;';
        el.cartBtn.style.position = 'relative';
        el.cartBtn.appendChild(badge);
    }
    badge.textContent = count > 0 ? count : '';
    badge.style.display = count > 0 ? 'block' : 'none';
}

function openCartModal() {
    // Create drawer on demand if not present
    let drawer = document.getElementById('cartModal');
    if (!drawer) {
        drawer = document.createElement('div');
        drawer.id = 'cartModal';
        drawer.className = 'cart-drawer';
        drawer.setAttribute('aria-hidden', 'true');
        drawer.innerHTML = `
            <div class="cart-backdrop"></div>
            <div class="cart-panel">
                <div class="cart-header">
                    <div>
                        <p class="cart-eyebrow">Your Selection</p>
                        <h2 class="cart-title">Cart</h2>
                    </div>
                    <button class="cart-close" id="cartCloseBtn" aria-label="Close cart">
                        <span class="close-x" aria-hidden="true"><span></span><span></span></span>
                    </button>
                </div>
                <div class="cart-items" id="cartItems"></div>
                <div class="cart-footer">
                    <div id="cartTotal" class="cart-total"></div>
                    <button class="nav-cta cart-checkout-btn" id="cartCheckoutBtn">Proceed to Private Checkout</button>
                    
                    <div class="cart-assistance">
                        <p class="assistance-eyebrow">Client Assistance</p>
                        <div class="assistance-grid">
                            <button class="assistance-link-minimal" id="cartAssistanceChat">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                </svg>
                                <span>Chat</span>
                            </button>
                            <button class="assistance-link-minimal" id="cartAssistanceSpeak">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                </svg>
                                <span>Inquiry</span>
                            </button>
                            <button class="assistance-link-minimal" id="cartAssistanceAppointment">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                                <span>Appointment</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(drawer);

        // Force a reflow so the initial off-screen state (translateX(100%)) is painted.
        // This ensures the transform transition runs smoothly on the *first* open.
        void drawer.offsetWidth;

        // Click on backdrop closes
        const backdrop = drawer.querySelector('.cart-backdrop');
        if (backdrop) {
            backdrop.addEventListener('click', closeCartModal);
        }
    }

    // Populate content
    const itemsEl = drawer.querySelector('#cartItems');
    const totalEl = drawer.querySelector('#cartTotal');
    itemsEl.innerHTML = '';

    if (cart.length === 0) {
        itemsEl.innerHTML = '<p style="opacity:0.6; padding: 1rem 0;">Your cart is empty.</p>';
        totalEl.textContent = '';
    } else {
        let total = 0;
        cart.forEach((item, index) => {
            const itemTotal = (item.price || 0) * (item.qty || 1);
            total += itemTotal;
            const div = document.createElement('div');
            div.style.cssText = 'display:flex;justify-content:space-between;align-items:flex-start;padding:0.85rem 0;border-bottom:1px solid rgba(255,255,255,0.1);';
            div.innerHTML = `
                <div style="flex:1; padding-right:1rem;">
                    <div style="font-size:0.9rem;letter-spacing:0.03em; line-height:1.3;">${item.title}</div>
                    <div style="font-size:0.7rem;opacity:0.55; margin-top:2px;">${item.category} × ${item.qty || 1}</div>
                </div>
                <div style="text-align:right; white-space:nowrap;">
                    <div style="font-size:0.85rem; color:var(--clr-gold);">€${itemTotal}</div>
                    <button class="remove-cart-item" data-index="${index}" style="font-size:0.65rem;opacity:0.5;background:none;border:none;color:inherit;cursor:pointer; margin-top:4px; padding:2px 0;">Remove</button>
                </div>
            `;
            itemsEl.appendChild(div);
        });
        totalEl.innerHTML = `<span style="opacity:0.6;">Total</span> <strong style="font-weight:400; letter-spacing:0.05em;">€${total}</strong>`;

        // Remove handlers
        itemsEl.querySelectorAll('.remove-cart-item').forEach(btn => {
            btn.addEventListener('click', () => {
                const idx = parseInt(btn.dataset.index);
                cart.splice(idx, 1);
                updateCartUI();
                openCartModal(); // refresh
            });
        });
    }

    drawer.removeAttribute('aria-hidden');
    addClass(drawer, 'is-open');

    // Close button
    const closeBtn = drawer.querySelector('#cartCloseBtn');
    if (closeBtn) {
        closeBtn.onclick = () => closeCartModal();
    }

    // Checkout
    const checkout = drawer.querySelector('#cartCheckoutBtn');
    if (checkout) {
        checkout.onclick = () => {
            alert('Thank you. A private concierge will contact you shortly to confirm your selection.');
            cart = [];
            updateCartUI();
            closeCartModal();
        };
    }

    // Client Assistance in Cart
    const cartChat = drawer.querySelector('#cartAssistanceChat');
    const cartSpeak = drawer.querySelector('#cartAssistanceSpeak');
    const cartAppoint = drawer.querySelector('#cartAssistanceAppointment');

    if (cartChat) {
        cartChat.onclick = () => alert('A member of the House is joining the conversation.');
    }
    if (cartSpeak) {
        cartSpeak.onclick = () => {
            closeCartModal();
            const contactNav = document.getElementById('navContact');
            if (contactNav) contactNav.click();
        };
    }
    if (cartAppoint) {
        cartAppoint.onclick = () => {
            closeCartModal();
            if (typeof openAppointmentModal === 'function') openAppointmentModal();
        };
    }
}

function closeCartModal() {
    const drawer = document.getElementById('cartModal');
    if (drawer) {
        removeClass(drawer, 'is-open');
        drawer.setAttribute('aria-hidden', 'true');
    }
}

/* ==============================================
   PRODUCT CARD HOVER VIDEOS
   ============================================== */
function initProductHoverVideos() {
    document.querySelectorAll('.product-card').forEach(card => {
        const video = card.querySelector('.product-video');
        if (!video) return;

        card.addEventListener('mouseenter', () => {
            video.muted = true;
            video.play().catch(() => {});
        });

        card.addEventListener('mouseleave', () => {
            video.pause();
            video.currentTime = 0;
        });
    });
}


function initArchiveSystem() {
    const filterButtons = document.querySelectorAll('.filter-btn');
    const archiveItems = document.querySelectorAll('.archive-item');
    const archiveContent = document.getElementById('archiveContent');

    // 1. Curated Category Filters
    filterButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const category = btn.getAttribute('data-category');

            // Update active button state
            filterButtons.forEach(b => removeClass(b, 'is-active'));
            addClass(btn, 'is-active');

            // Smoothly filter cards
            archiveItems.forEach(item => {
                const itemCategory = item.getAttribute('data-category');
                
                // If it matches or category is 'all'
                if (category === 'all' || itemCategory === category) {
                    removeClass(item, 'is-hidden');
                } else {
                    addClass(item, 'is-hidden');
                }
            });

            // Scroll container back to the top smoothly
            if (archiveContent) {
                archiveContent.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    });

    // 2. Interactive Product Card Clicks
    const productCards = document.querySelectorAll('.product-card');
    productCards.forEach(card => {
        card.addEventListener('click', (e) => {
            if (e.target.closest('.add-to-cart-btn')) return;
            if (e.target.closest('[data-ignore-product-open]')) {
                return;
            }
            openProductDetail(card);
        });
    });

    // 2a. Add to Cart buttons (Collection page, real Shopify cart) are
    // handled separately by cmc-cart.js via document-level delegation.

    // 2b. Hover Product Videos Playback
    productCards.forEach(card => {
        const video = card.querySelector('.product-video');
        if (!video) return;

        card.addEventListener('mouseenter', () => {
            video.muted = true;
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.log('Product video autoplay prevented:', error);
                });
            }
        });

        card.addEventListener('mouseleave', () => {
            video.pause();
            video.currentTime = 0;
        });
    });

    // 3. Detail View Navigation Back Link
    if (el.detailBackBtn) {
        el.detailBackBtn.addEventListener('click', () => {
            closeProductDetail();
        });
    }

    // 4. Appointment Booking / Bespoke Fitting Trigger inside Detail View
    if (el.detailInquireBtn) {
        el.detailInquireBtn.addEventListener('click', () => {
            openAppointmentModal();
        });
    }

    // 4b. Add to Cart from Detail View is handled by cmc-cart.js via
    // document-level delegation on .add-to-cart-btn (this button's
    // data-variant-id / data-product-handle are set dynamically by
    // openProductDetail() above, same as the grid buttons).

    // 5. Scroll-driven Image Zoom
    const detailScrollContainer = document.getElementById('detailScrollContainer');
    if (detailScrollContainer) {
        detailScrollContainer.addEventListener('scroll', () => {
            const scrollTop = detailScrollContainer.scrollTop;
            const scrollScale = 1 + Math.min(scrollTop * 0.0006, 0.45);
            const img = el.detailImgWrap ? (el.detailImgWrap.querySelector('.swiper-slide-active img') || el.detailImgWrap.querySelector('img')) : null;
            if (img) {
                img.style.setProperty('--scroll-scale', 1);
            }
        });
    }

    // 6. Read More Expander for narrative
    const storyMoreBtn = document.getElementById('storyMoreBtn');
    const storyEl = document.getElementById('detailProductStory');
    if (storyMoreBtn && storyEl) {
        storyMoreBtn.addEventListener('click', () => {
            const isExpanded = storyEl.classList.contains('is-expanded');
            const textSpan = storyMoreBtn.querySelector('.btn-text');
            const iconSpan = storyMoreBtn.querySelector('.btn-icon');
            if (isExpanded) {
                removeClass(storyEl, 'is-expanded');
                storyMoreBtn.setAttribute('aria-expanded', 'false');
                if (textSpan) textSpan.textContent = 'Read More';
                if (iconSpan) iconSpan.textContent = '↓';
            } else {
                addClass(storyEl, 'is-expanded');
                storyMoreBtn.setAttribute('aria-expanded', 'true');
                if (textSpan) textSpan.textContent = 'Read Less';
                if (iconSpan) iconSpan.textContent = '↑';
            }
        });
    }

    // 7. Pan-on-Hover / Touch Image Zoom Exploration
if (el.detailImgWrap) {

    function updatePosition(clientX, clientY) {
        const rect = el.detailImgWrap.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) * 100;
        const y = ((clientY - rect.top) / rect.height) * 100;
        const img = el.detailImgWrap.querySelector('.swiper-slide-active img') || el.detailImgWrap.querySelector('img');
        if (img) {
            img.style.transformOrigin = `${x}% ${y}%`;
        }
    }

    const isMobile = () => window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    // ── Desktop only: hover zoom ──────────────────────────────────────
    el.detailImgWrap.addEventListener('mousemove', (e) => {
        if (isMobile()) return;
        updatePosition(e.clientX, e.clientY);
    });

    el.detailImgWrap.addEventListener('mouseleave', () => {
        if (isMobile()) return;
        const img = el.detailImgWrap.querySelector('.swiper-slide-active img') || el.detailImgWrap.querySelector('img');
        if (img) img.style.transformOrigin = 'center center';
    });

    // ── Mobile only: tap to open full-viewport modal ──────────────────
    el.detailImgWrap.addEventListener('click', (e) => {
        if (!isMobile()) return;

        const img = el.detailImgWrap.querySelector('.swiper-slide-active img') || el.detailImgWrap.querySelector('img');
        if (!img) return;

        // Build modal
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            inset: 0;
            z-index: 99999;
            background: rgba(0, 0, 0, 0.95);
            display: flex;
            align-items: center;
            justify-content: center;
            touch-action: none;
            max-width: 100vw;
            display: none;
        `;

        const modalImg = document.createElement('img');
        modalImg.src = img.src;
        modalImg.alt = img.alt || '';
        modalImg.style.cssText = `
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
            display: block;
        `;

        const closeBtn = document.createElement('button');
        closeBtn.innerHTML = '&times;';
        closeBtn.setAttribute('aria-label', 'Close image');
        closeBtn.style.cssText = `
            position: absolute;
            top: 16px;
            right: 20px;
            background: transparent;
            border: none;
            color: #fff;
            font-size: 2.5rem;
            line-height: 1;
            cursor: pointer;
            padding: 8px;
            z-index: 100000;
            text-shadow:
                0 0 3px rgba(0,0,0,0.9),
                0 0 8px rgba(0,0,0,0.6);
            mix-blend-mode: difference;
            filter: invert(0);
        `;

        const closeModal = () => {
            document.body.removeChild(overlay);
            document.body.style.overflow = '';
        };

        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            closeModal();
        });

        // Tapping the backdrop also closes
        overlay.addEventListener('click', closeModal);
        modalImg.addEventListener('click', (e) => e.stopPropagation());

        overlay.appendChild(closeBtn);
        overlay.appendChild(modalImg);
        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden'; // prevent background scroll
    });
}
}

/* ==============================================
   APPOINTMENT SYSTEM
   ============================================== */
function initAppointmentSystem() {
    if (el.appointmentBtn) {
        el.appointmentBtn.addEventListener('click', openAppointmentModal);
    }
    if (el.appointmentCloseBtn) {
        el.appointmentCloseBtn.addEventListener('click', closeAppointmentModal);
    }
    if (el.appointmentOverlay) {
        el.appointmentOverlay.addEventListener('click', e => {
            if (e.target === el.appointmentOverlay) closeAppointmentModal();
        });
    }
}

function openAppointmentModal() {
    if (el.appointmentOverlay) {
        addClass(el.appointmentOverlay, 'is-open');
        el.appointmentOverlay.removeAttribute('aria-hidden');
    }
    // Dismiss registry quietly when opening appointment (user chose deeper interaction)
    if (el.registryPanel && el.registryPanel.classList.contains('is-visible') && !state.registryJoined) {
        dismissRegistryPanel(false);
    }
}

function closeAppointmentModal() {
    if (el.appointmentOverlay) {
        removeClass(el.appointmentOverlay, 'is-open');
        el.appointmentOverlay.setAttribute('aria-hidden', 'true');
    }
}

/* ==============================================
   CONTACT SYSTEM
   ============================================== */
function initContactSystem() {
    const form = document.getElementById('contactForm');
    const successEl = document.getElementById('contactSuccess');

    if (!form) return;

    form.addEventListener('submit', e => {
        e.preventDefault();

        const nameVal = form.querySelector('#contactName').value.trim();
        const emailVal = form.querySelector('#contactEmail').value.trim();

        if (!nameVal || !emailVal) {
            if (!nameVal) form.querySelector('#contactName').focus();
            else if (!emailVal) form.querySelector('#contactEmail').focus();
            return;
        }

        addClass(form, 'is-hidden');

        setTimeout(() => {
            if (successEl) {
                successEl.removeAttribute('aria-hidden');
                addClass(successEl, 'is-visible');
            }
        }, 620);
    });
}

/* ==============================================
   HOUSE REGISTRY SYSTEM
   Luxury, non-intrusive email capture.
   Triggered by engagement + journey, not timers.
   Persists via localStorage. Cinematic motion only.
   ============================================== */

const REGISTRY_LS_DISMISSED = 'houseRegistryDismissed';
const REGISTRY_LS_JOINED = 'houseRegistryJoined';
const REGISTRY_MIN_ENGAGE_MS = 52000; // ~52s within 45-70s range
const REGISTRY_SCROLL_THRESHOLD = 0.55;

function initRegistrySystem() {
    // Restore persisted state
    if (localStorage.getItem(REGISTRY_LS_JOINED) === 'true') {
        state.registryJoined = true;
    }
    // Note: LS_DISMISSED suppresses only auto engagement popups.
    // Explicit "Enter the Registry" button (force) can still open the panel.

    updateRegistryTriggerVisibility();

    // Wire primary panel
    if (el.registryCloseBtn) {
        el.registryCloseBtn.addEventListener('click', () => dismissRegistryPanel(true));
    }
    if (el.registryPanel) {
        el.registryPanel.addEventListener('click', e => {
            if (e.target === el.registryPanel) dismissRegistryPanel(true);
        });
    }
    if (el.registryForm) {
        el.registryForm.addEventListener('submit', handleRegistrySubmit);
    }

    // Salon bottom-right trigger (opposite Editorial Block) — primary secondary entry point
    if (el.registrySalonTrigger) {
        el.registrySalonTrigger.addEventListener('click', () => {
            showRegistryPanel(true);
        });
    }

    // Inline archive form
    if (el.registryInlineForm) {
        el.registryInlineForm.addEventListener('submit', handleRegistryInlineSubmit);
    }

    // Track section visits on phase changes (hooked from transitions)
    // Scroll depth on scrollable archive/journal content
    const archiveContent = document.getElementById('archiveContent');
    const journalContent = document.getElementById('journalContent');
    const collectionContent = document.getElementById('collectionContent');

    function updateScrollDepth(container) {
        if (!container) return;
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight - container.clientHeight;
        if (scrollHeight <= 0) return;
        const depth = Math.min(1, scrollTop / scrollHeight);
        if (depth > state.scrollDepth) {
            state.scrollDepth = depth;
            maybeShowRegistry();
        }
    }

    if (archiveContent) {
        archiveContent.addEventListener('scroll', () => updateScrollDepth(archiveContent), { passive: true });
    }
    if (journalContent) {
        journalContent.addEventListener('scroll', () => updateScrollDepth(journalContent), { passive: true });
    }
    if (collectionContent) {
        collectionContent.addEventListener('scroll', () => updateScrollDepth(collectionContent), { passive: true });
    }

    // Keyboard support (global escape already handles some, but ensure for panel)
    document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && el.registryPanel && el.registryPanel.classList.contains('is-visible')) {
            dismissRegistryPanel(true);
        }
        if (e.key === 'Enter' && document.activeElement && el.registryPanel && el.registryPanel.contains(document.activeElement)) {
            // allow natural form submit
        }
    });

    // Start quiet engagement watcher after salon is revealed
    // We poll lightly; real triggers are event driven too
    setTimeout(() => {
        startRegistryEngagementWatcher();
    }, 8000);
}

function startRegistryEngagementWatcher() {
    // Check conditions periodically after initial salon time
    const interval = setInterval(() => {
        if (state.registryShown || state.registryJoined) {
            clearInterval(interval);
            return;
        }
        maybeShowRegistry();
    }, 6500);

    // Also check on visibility changes / focus (user returned after deep reading)
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden && !state.registryShown && !state.registryJoined) {
            maybeShowRegistry();
        }
    });
}

function recordSectionVisit(section) {
    if (!section) return;
    state.visitedSections.add(section);
    maybeShowRegistry();
}

function maybeShowRegistry(force = false) {
    if (state.registryShown || state.registryJoined) return;
    if (localStorage.getItem(REGISTRY_LS_DISMISSED) === 'true') return;
    if (localStorage.getItem(REGISTRY_LS_JOINED) === 'true') {
        state.registryJoined = true;
        return;
    }

    const engagedMs = Date.now() - state.engagementStart;
    const hasDeepVisit = state.visitedSections.has('archive') ||
                         state.visitedSections.has('journal') ||
                         state.visitedSections.has('collection');
    const hasScroll = state.scrollDepth >= REGISTRY_SCROLL_THRESHOLD;
    const hasTime = engagedMs >= REGISTRY_MIN_ENGAGE_MS;

    // Primary trigger: after Editorial Discovery + meaningful progression
    // Editorial discovery is represented by having spent time in salon + at least one deep section or scroll
    const ready = hasTime && (hasDeepVisit || hasScroll);

    if (force || ready) {
        showRegistryPanel();
    }
}

function resetRegistryForm() {
    if (el.registryForm) {
        el.registryForm.reset();
        removeClass(el.registryForm, 'is-hidden');
    }
    if (el.registrySuccess) {
        el.registrySuccess.setAttribute('aria-hidden', 'true');
        removeClass(el.registrySuccess, 'is-visible');
    }
}

function showRegistryPanel(forceFromContext = false) {
    if (!el.registryPanel) return;
    if (state.registryJoined && !forceFromContext) return;
    if (state.registryShown && !forceFromContext) return;
    if (localStorage.getItem(REGISTRY_LS_DISMISSED) === 'true' && !forceFromContext) return;

    // Never show while loading or early video phases
    if (state.phase === 'loading' || state.phase === 'video1' || state.phase === 'exploring') return;

    // Hide if a heavy overlay is open (appointment, detail, sound) — but allow force override for explicit triggers
    if (!forceFromContext && el.appointmentOverlay && el.appointmentOverlay.classList.contains('is-open')) return;
    if (document.body.classList.contains('detail-active')) return;

    // Reset the form so it is ready for another submission
    resetRegistryForm();

    addClass(el.registryPanel, 'is-visible');
    el.registryPanel.removeAttribute('aria-hidden');

    state.registryShown = true;
}

function dismissRegistryPanel(userInitiated = true) {
    if (!el.registryPanel) return;
    removeClass(el.registryPanel, 'is-visible');
    el.registryPanel.setAttribute('aria-hidden', 'true');

    if (userInitiated) {
        localStorage.setItem(REGISTRY_LS_DISMISSED, 'true');
        // Reset session flag so explicit "Enter the Registry" button can re-open the panel.
        // Auto engagement popups remain suppressed by the LS_DISMISSED key.
        state.registryShown = false;
    }
    // Quietly reset the form after transition has completed
    setTimeout(resetRegistryForm, 1000);
}

function handleRegistrySubmit(e) {
    e.preventDefault();
    if (!el.registryForm) return;

    const nameInput = document.getElementById('registryName');
    const emailInput = document.getElementById('registryEmail');

    const name = nameInput ? nameInput.value.trim() : '';
    const email = emailInput ? emailInput.value.trim() : '';

    if (!name || !email || !email.includes('@')) {
        if (emailInput && (!email || !email.includes('@'))) emailInput.focus();
        else if (nameInput) nameInput.focus();
        return;
    }

    // Simulate quiet submission (no network, pure ceremony)
    submitRegistryEntry(name, email, el.registryForm, el.registrySuccess, () => {
        localStorage.setItem(REGISTRY_LS_JOINED, 'true');
        state.registryJoined = true;
        updateRegistryTriggerVisibility();
        // After success, quietly dismiss panel after a breath
        setTimeout(() => {
            dismissRegistryPanel(false);
        }, 1650);
    });
}

function handleRegistryInlineSubmit(e) {
    e.preventDefault();
    if (!el.registryInlineForm || !el.registryInlineArchive) return;

    const form = el.registryInlineForm;
    const nameInput = form.querySelector('input[name="name"]');
    const emailInput = form.querySelector('input[name="email"]');

    const name = nameInput ? nameInput.value.trim() : '';
    const email = emailInput ? emailInput.value.trim() : '';

    if (!name || !email || !email.includes('@')) {
        if (emailInput && (!email || !email.includes('@'))) emailInput.focus();
        else if (nameInput) nameInput.focus();
        return;
    }

    const successEl = el.registryInlineArchive.querySelector('.registry-inline-success');

    submitRegistryEntry(name, email, form, successEl, () => {
        localStorage.setItem(REGISTRY_LS_JOINED, 'true');
        state.registryJoined = true;
        updateRegistryTriggerVisibility();
        // Keep the inline block visible but completed (no auto hide for editorial feel)
        // Optionally fade the form area
        setTimeout(() => {
            if (form) addClass(form, 'is-hidden');
        }, 420);

        // Reset the inline form after a few seconds so they can register again
        setTimeout(() => {
            // Fade out success
            removeClass(successEl, 'is-visible');
            successEl.setAttribute('aria-hidden', 'true');
            
            // Wait for success fade to finish, then restore form
            setTimeout(() => {
                form.reset();
                removeClass(form, 'is-hidden');
            }, 600);
        }, 4000);
    });
}

function submitRegistryEntry(name, email, formEl, successEl, onComplete) {
    // Graceful, non-jarring: hide form, reveal success
    if (formEl) addClass(formEl, 'is-hidden');

    setTimeout(() => {
        if (successEl) {
            successEl.removeAttribute('aria-hidden');
            addClass(successEl, 'is-visible');
        }
        if (onComplete) onComplete();
    }, 380);
}

/* Hook: call this from phase transitions to record visits */
function markRegistrySectionVisit(phase) {
    if (phase === 'archive') recordSectionVisit('archive');
    else if (phase === 'journal') recordSectionVisit('journal');
    else if (phase === 'collection') recordSectionVisit('collection');
}

function updateRegistryTriggerVisibility() {
    if (!el.registrySalonTrigger) return;
    const shouldShow = state.phase === 'salon';
    el.registrySalonTrigger.style.display = shouldShow ? '' : 'none';
}

/* ==============================================
   VIDEO PRELOAD OPTIMIZATION
   ============================================== */
function preloadAssets() {
    [el.video1, el.video2].forEach(v => {
        if (v) v.setAttribute('preload', 'auto');
    });

    const imgPrefetch = [assets.heroBg, assets.hallwayEnd, assets.salonEnd];
    imgPrefetch.forEach(src => {
        if (!src) return;
        const img = new Image();
        img.src = src;
    });
}

/* ==============================================
   MOBILE MEDIA SOURCES — swap assets for mobile
   ============================================== */
function applyMobileMediaSources() {
    if (!isMobile) return;

    if (el.video1) {
        el.video1.src = assets.video1;
        el.video1.poster = assets.hallwayEnd;
        el.video1.load();
    }

    if (el.hallwayFreeze) {
        el.hallwayFreeze.style.backgroundImage = `url('${assets.hallwayEnd}')`;
    }

    const salonBg = document.querySelector('#phaseSalon .phase-bg');
    if (salonBg) {
        salonBg.style.backgroundImage = `url('${assets.hallwayEnd}')`;
    }

    const salonFreezeBg = el.salonFreeze;
    if (salonFreezeBg) {
        salonFreezeBg.style.backgroundImage = `url('${assets.hallwayEnd}')`;
    }
}

/* ==============================================
   MOBILE / TOUCH OPTIMIZATIONS
   ============================================== */
function applyMobileOptimizations() {
    const isTouchDevice = 'ontouchstart' in window;

    if (isMobile) {
        document.querySelectorAll('.phase-bg').forEach(bg => {
            bg.style.animation = 'none';
        });
    }

    if (isTouchDevice) {
        document.querySelectorAll('a, button, .sound-toggle').forEach(el => {
            el.style.cursor = 'pointer';
        });
    }
}

/* ==============================================
   RESIZE HANDLER
   ============================================== */
(function initResizeHandler() {
    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            applyMobileOptimizations();
        }, 200);
    }, { passive: true });
})();

/* ==============================================
   COLLECTION PAGE FILTERS (scoped)
   ============================================== */
function initCollectionFilters() {
    const collectionContent = document.getElementById('collectionContent');
    if (!collectionContent) return;

    const filterButtons = collectionContent.parentElement.querySelectorAll('.archive-filter .filter-btn');
    const collectionItems = collectionContent.querySelectorAll('.archive-item');

    filterButtons.forEach(btn => {
        btn.addEventListener('click', () => {
            const category = btn.getAttribute('data-category');

            // Update active state only within Collection filters
            filterButtons.forEach(b => b.classList.remove('is-active'));
            btn.classList.add('is-active');

            collectionItems.forEach(item => {
                const itemCategory = item.getAttribute('data-category');
                if (category === 'all' || itemCategory === category) {
                    item.classList.remove('is-hidden');
                } else {
                    item.classList.add('is-hidden');
                }
            });

            collectionContent.scrollTo({ top: 0, behavior: 'smooth' });
        });
    });
}

/* ==============================================
   CLIENT ASSISTANCE SYSTEM
   ============================================== */
function initClientAssistance() {
    // Product Page Buttons
    const chatBtn = document.getElementById('assistanceChat');
    const speakBtn = document.getElementById('assistanceSpeak');
    const appointBtn = document.getElementById('assistanceAppointment');

    if (chatBtn) {
        chatBtn.addEventListener('click', () => {
            alert('A member of the House is joining the conversation. Please wait a moment.');
        });
    }

    if (speakBtn) {
        speakBtn.addEventListener('click', () => {
            const contactNav = document.getElementById('navContact');
            if (contactNav) contactNav.click();
        });
    }

    if (appointBtn) {
        appointBtn.addEventListener('click', () => {
            if (typeof openAppointmentModal === 'function') {
                openAppointmentModal();
            }
        });
    }
}

/* ==============================================
   INITIALISATION
   ============================================== */
function init() {
    // Each subsystem is wrapped so that a missing section (e.g. during a
    // section-by-section rollout, before every "Cinematic ..." section has
    // been added/published) can't throw and prevent the rest of the page
    // -- especially the loading/video sequence -- from initializing.
    const safeInit = (name, fn) => {
        try {
            fn();
        } catch (err) {
            console.warn('[cinematic] skipped ' + name + ' (likely missing section/elements):', err);
        }
    };

    safeInit('applyMobileMediaSources', applyMobileMediaSources);
    safeInit('preloadAssets', preloadAssets);
    safeInit('applyMobileOptimizations', applyMobileOptimizations);
    safeInit('initSoundSystem', initSoundSystem);
    safeInit('initMenuSystem', initMenuSystem);
    safeInit('initAppointmentSystem', initAppointmentSystem);
    safeInit('initArchiveSystem', initArchiveSystem);
    safeInit('initJournalSystem', initJournalSystem);
    safeInit('initContactSystem', initContactSystem);
    safeInit('initRegistrySystem', initRegistrySystem);
    safeInit('initClientAssistance', initClientAssistance);

    // Collection page filters (scoped to collectionContent)
    safeInit('initCollectionFilters', initCollectionFilters);

    // Product card hover videos — runs on all pages independently
    safeInit('initProductHoverVideos', initProductHoverVideos);

    if (el.continueExploringBtn) {
        el.continueExploringBtn.addEventListener('click', onContinueExploring);
    }

    // Cart icon links to the real Shopify cart page (see cartBtn markup).
    // The fake in-memory cart drawer is disabled in favor of Shopify's cart.
    // if (el.cartBtn) {
    //     el.cartBtn.addEventListener('click', openCartModal);
    // }

    // Initial cart UI
    // updateCartUI();

    safeInit('runLoadingSequence', () => { runLoadingSequence(); });
}

document.addEventListener('DOMContentLoaded', init);